import { useSelector } from "react-redux";
import { RootState } from "../../../store";
import { Cell, Pie, PieChart, ResponsiveContainer } from "recharts";
import { generateColorFromName } from "../../../utils/utils";
import { formMappings } from "../../../config/formMappings";

export default function EthnicityDistributionGraph() {
  const entries = useSelector((state: RootState) => state.formEntries.entries);

  const activeFormName = useSelector(
    (state: RootState) => state.formEntries.activeFormName || ""
  );

  const formMapping = formMappings[activeFormName as keyof typeof formMappings];
  const version = formMapping?.version || "1";
  const ethnicityFieldKey =
    (formMapping?.fieldMappings as any)?.Ethnicity || "b3bic3";
  const registrantsFieldKey =
    (formMapping?.fieldMappings as any)?.Registrants || "9v3kh";

  // Calculate ethnicity distribution
  const ethnicityDistribution = entries.reduce((acc, entry) => {
    if (version === "1") {
      // Version 1: Ethnicity is at the top level
      const ethnicities = entry.meta.Ethnicity;
      if (ethnicities) {
        // Handle both array and string cases for Ethnicity
        if (Array.isArray(ethnicities)) {
          ethnicities.forEach((ethnicity) => {
            acc[ethnicity] = (acc[ethnicity] || 0) + 1;
          });
        } else if (typeof ethnicities === "string") {
          acc[ethnicities] = (acc[ethnicities] || 0) + 1;
        }
      }
    } else {
      // Version 2+: Ethnicity exists on every registrant
      // First, try to find registrants in the mapped "Registrants" key
      if (entry.meta.Registrants) {
        Object.values(entry.meta.Registrants)
          .filter(
            (registrant) => registrant && (registrant as any)[ethnicityFieldKey]
          )
          .forEach((registrant) => {
            const ethnicity = (registrant as any)[ethnicityFieldKey];
            if (ethnicity) {
              // Split by commas and treat each as a separate ethnicity
              let ethnicities: string[] = [];
              if (Array.isArray(ethnicity)) {
                // If it's an array, flatten and split each element by comma
                ethnicities = ethnicity
                  .flatMap((e: any) =>
                    typeof e === "string"
                      ? e
                          .split(",")
                          .map((item) => item.trim())
                          .filter((item) => item)
                      : [String(e)]
                  )
                  .filter((e) => e);
              } else if (typeof ethnicity === "string") {
                // If it's a string, split by comma
                ethnicities = ethnicity
                  .split(",")
                  .map((e) => e.trim())
                  .filter((e) => e);
              } else {
                ethnicities = [String(ethnicity)];
              }
              ethnicities.forEach((eth: string) => {
                if (eth) {
                  acc[eth] = (acc[eth] || 0) + 1;
                }
              });
            }
          });
      } else {
        // Fallback: look for registrants using the Registrants field key from formMappings
        // Extract base prefix by removing trailing digits to match variations (e.g., "9v3kh" from "9v3kh2")
        // This will match both "9v3kh2" and "9v3kh32" in the actual data
        const basePrefix = registrantsFieldKey.replace(/\d+$/, "");
        const registrantsKey = Object.keys(entry.meta).find(
          (key) =>
            (key === registrantsFieldKey || key.startsWith(basePrefix)) &&
            typeof entry.meta[key] === "object"
        );

        if (registrantsKey) {
          const registrantsContainer = entry.meta[registrantsKey] as any;
          if (registrantsContainer) {
            Object.values(registrantsContainer)
              .filter(
                (item: any) =>
                  item &&
                  typeof item === "object" &&
                  item[ethnicityFieldKey] &&
                  item[ethnicityFieldKey] !== "form"
              )
              .forEach((registrant: any) => {
                const ethnicity = registrant[ethnicityFieldKey];
                if (ethnicity) {
                  // Split by commas and treat each as a separate ethnicity
                  let ethnicities: string[] = [];
                  if (Array.isArray(ethnicity)) {
                    // If it's an array, flatten and split each element by comma
                    ethnicities = ethnicity
                      .flatMap((e: any) =>
                        typeof e === "string"
                          ? e
                              .split(",")
                              .map((item) => item.trim())
                              .filter((item) => item)
                          : [String(e)]
                      )
                      .filter((e) => e);
                  } else if (typeof ethnicity === "string") {
                    // If it's a string, split by comma
                    ethnicities = ethnicity
                      .split(",")
                      .map((e) => e.trim())
                      .filter((e) => e);
                  } else {
                    ethnicities = [String(ethnicity)];
                  }
                  ethnicities.forEach((eth: string) => {
                    if (eth) {
                      acc[eth] = (acc[eth] || 0) + 1;
                    }
                  });
                }
              });
          }
        }
      }
    }
    return acc;
  }, {} as { [ethnicity: string]: number });

  const ethnicityData = Object.entries(ethnicityDistribution).map(
    ([ethnicity, count]) => ({
      name: ethnicity,
      value: count,
    })
  );

  return (
    <div className="overflow-hidden rounded-lg bg-white px-4 py-5 shadow sm:p-6 col-span-2">
      <h1 className="text-xl text-primary text-center">
        Ethnicity Distribution
      </h1>
      <ResponsiveContainer width="100%" height="100%">
        <PieChart width={400} height={400}>
          <Pie
            data={ethnicityData}
            dataKey="value"
            cx="50%"
            cy="50%"
            outerRadius={100}
            fill="#1b3356"
            label={({ name, value }) => `${name}: ${value}`}
          >
            {ethnicityData.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={generateColorFromName(entry.name)}
              />
            ))}
          </Pie>
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
}
